import {
  isDev,
  commonSourceParam,
  git_branch,
  release_version,
  getSlardarInitContexts,
} from '@meego/utils/src/platform/environment';
import { ENV } from '@meego/utils/src/envs';
import { createPromiseObj } from '@meego/utils/src/tools/common';
// import { guardWorker } from './healthGuard';
import pathToRegexp from 'path-to-regexp';
import { slardarBid } from './slardarBid';

export const SLARDAR_CLIENT = 'Slardar';
const SLARDAR_ACTION_WITH_CUSTOM_EVENT_TYPE = '_slardar_action_with_custom_event_';

// 移除像域名之类的敏感词，避免被脱敏插件识别误伤
function replaceSensitiveWord(content: string): string {
  return content.replace(ENV.currentHost, '#{host}');
}
declare global {
  interface Window {
    Slardar?: ((...params: any[]) => void) & { config: () => Record<string, any> };
  }
}
type SlardarConfig = {
  // required
  bid: string;
  // optional, the following values are by default
  domain: string;
  pid: string;
  userId: string;
  slardar_session_id: string;
  ignoreAjax: string[];
  hookRequest: boolean;
  enableCatchJSError: boolean;
  enablePerformance: boolean;
  performanceAuto: boolean; // use the default timing to report performance, same as browser SDK
  enableSizeStats: boolean;
};

const PathList = [
  '/:project/story/detail/:id',
  '/:project/issue/detail/:id',
  '/:project/chart/detail/:id',
  '/:project/chartdatascope/detail/:id',
  '/:project/chartpenetrate/detail/:id',
  '/:project/:workobject/detail/:id',

  '/:project/storyView/:viewid',
  '/:project/storyView',
  '/:project/issueView/:viewid',
  '/:project/issueView',
  '/:project/userGantt/:viewid',
  '/:project/userGantt',
  '/:project/workObjectView/chart/:storyViewId',
  '/:project/workObjectView/chart',
  '/:project/workObjectView/:workobject',
  '/:project/workObjectView/:workobject/:viewid', // 工作项视图

  '/:project/chart/create',
  '/:project/:workobject/create',
  '/:project/:workobject/draft',
  '/:project/story/create',
  '/:project/story/create/draft',
  '/:project/issue/create',
  '/:project/issue/draft',
  '/:project/:workobject/create',
  '/:project/:workobject/draft',
  '/:project/meegoPlg/:plgKey',
  '/:project/setting',
  '/:project/setting/:tab',
  '/:project/setting/workObject/:workobject',
  '/workbench',
  '/home',
  '/:project/404',
  '/:project/401',
  '/:project/500',
  '/',
];

const PathRegexpArray = PathList.map(item => [pathToRegexp(item), item]);
function extractPid(pathname: string): string {
  if (typeof pathname !== 'string' || !pathname) {
    return pathname;
  }
  const matchRes = PathRegexpArray.find(([regexp, target]) =>
    (regexp as pathToRegexp.PathRegExp).test(pathname) ? target : '',
  );
  return matchRes ? (matchRes[1] as string) : pathname;
}

export class Slardar {
  // slardar 是否已经开始

  _started: boolean = false;

  private customInteractionTags: Record<string, Record<string, any> | undefined> = {};

  private interceptors: Function[] = [];

  private initPromise: Promise<boolean>;

  private initConfigPromise: ReturnType<typeof createPromiseObj>;

  constructor() {
    this.sendCustom = this.sendCustom.bind(this);
    this.sendLog = this.sendLog.bind(this);

    const { promise, resolve: initPromiseResolve } = createPromiseObj<boolean>();
    this.initPromise = promise;
    // 报错调用堆栈信息 默认是 10  - firefox 没有这个属性 会是 undefined
    if (Error?.stackTraceLimit && Error.stackTraceLimit < 20) {
      Error.stackTraceLimit = 60;
    }
    // 配置： https://slardar.cn.goofy.app/docs/sdk/config-detail/plugins-config
    const domainConfig = ENV?.slardarConfig || {};
    window[SLARDAR_CLIENT]?.('init', {
      bid: slardarBid,
      env: isDev ? 'boe' : 'production', // 区分不同环境
      release: release_version,
      ...domainConfig, // 自定义上报域名， sg 需要上报到sg机房
      plugins: {
        pageview: {
          routeMode: 'history',
          extractPid(url: string) {
            try {
              const parseUrl = new URL(url);
              return extractPid(parseUrl.pathname);
            } catch (error) {
              return url;
            }
          },
        },
        ajax: {
          autoWrap: true,
          ignoreUrls: ['mcs.ctobsnssdk.com'], // 忽略tea的埋点请求
        },
        action: true,
        resource: {
          // 忽略特定资源
          ignoreUrls: [/\*.svg/],
          // 慢会话阈值，慢会话时会跳过采样规则直接上报，便于排查问题
          slowSessionThreshold: 4000,
        },
        // FMP 指标
        fmp: {
          renderType: 'CSR', // 渲染类型
        },
      },
    });

    // 设置 batch 接口，批量上传的事件的数据等
    window[SLARDAR_CLIENT]?.('on', 'init', () => {
      // 发送器 Sender - 负责发送逻辑，如聚合上报，重试等功能
      const Sender = window[SLARDAR_CLIENT]?.getSender?.();
      // wait 和 size 优先级相同，谁先到达就上报
      // Navigator.sendBeacon 不能保证数据一定能上报
      // Sender?.setWait(4000) // 4000代表4000ms一次上报
      Sender?.setSize(80); // 80代表聚合粒度为80条数据一次上报
      initPromiseResolve(true);
    });

    if (window[SLARDAR_CLIENT]) {
      // https://slardar.cn.goofy.app/docs/sdk/config-detail/sdkapi#clientcontextset-key-string-value-any--void
      window[SLARDAR_CLIENT]('context.merge', {
        build: commonSourceParam,
        branch: git_branch,
        version: release_version,
        pwa_static_html: window.__MEEGO_TEMPLATE_TYPE__ === 'static',
        pwa_sw_active: !!navigator.serviceWorker?.controller,
        referrer: document.referrer || '', // 上报 referer, 可以用于区分是否是 sw.js reload 的场景
        devicePixelRatio: window.devicePixelRatio,
        ...getSlardarInitContexts(),
      });
      window[SLARDAR_CLIENT]?.('config', {
        userId: window.userInfo?.username,
      });
      // addBeforeSend2SlardarWeb(window[SLARDAR_CLIENT]);
    }
    const slardarEvents = [];
    const maxWaitTime = 5 * 1000;
    window[SLARDAR_CLIENT]?.('on', 'beforeSend', event => {
      slardarEvents.push(event);
      return false;
    });
    setInterval(() => {
      if (window.__backgroundInit || performance.now() > maxWaitTime) {
        if (slardarEvents.length > 0) {
          const events = slardarEvents.splice(0, 80);
          const { transport } = window[SLARDAR_CLIENT]?.config();
          transport.post({
            url: window[SLARDAR_CLIENT]?.getSender?.()?.getEndpoint?.(),
            data: JSON.stringify({
              ev_type: 'batch',
              list: events,
            }),
          });
        }
      }
    }, 1000);

    // 上报拦截
    // window[SLARDAR_CLIENT]?.('on', 'beforeSend', ev => {
    //   // 监听到长任务，去检索对应时间段内的长耗时函数（这里有两个策略 1: 单个函数调用函数时常超 30ms 2: 长任务周期内 单个函数被多次调用，综合耗时长 > 50ms）
    //   // 降噪 只关注 > 200ms 的长任务
    //   if (ev.ev_type === 'performance_longtask' && ev?.payload?.longtasks?.[0]?.duration > 200) {
    //     guardWorker.postMessage({
    //       type: 'long_task',
    //       message: {
    //         timestamp: ev?.common?.timestamp,
    //         longtask: {
    //           duration: ev?.payload?.longtasks?.[0]?.duration || 0,
    //           startTime: ev?.payload?.longtasks?.[0]?.startTime || 0,
    //         },
    //       },
    //     });
    //   }
    //   // 监听内存 五秒内持续上涨并且大于 200 MB
    //   if (ev.ev_type === 'custom' && ev?.payload?.name === 'memory_monitor_multiple') {
    //     guardWorker.postMessage({
    //       type: 'js_heap',
    //       message: JSON.parse(
    //         JSON.stringify({
    //           timestamp: performance.now(),
    //           metrics: ev?.payload?.metrics,
    //         }),
    //       ),
    //     });
    //   }
    //   return ev;
    // });
    window[SLARDAR_CLIENT]?.('on', 'beforeReport', ev => {
      for (const fn of this.interceptors) {
        if (fn(ev) === false) {
          // 拦截器返回 false 表示不上报
          return false;
        }
      }
      if (ev.ev_type === 'action') {
        // slardar原生的 action 不能加tags，这里骚操作一下
        const { payload } = ev;
        const { name } = payload.target;
        const actionType = payload.type;
        const tags = this.customInteractionTags[`${actionType}:${name}`];
        if (actionType === SLARDAR_ACTION_WITH_CUSTOM_EVENT_TYPE) {
          const metrics: Record<string, number> = {};
          if (!payload.duration) {
            return;
          }
          if (payload.duration) {
            metrics.duration = payload.duration;
          }
          if (payload.frontend_time) {
            metrics.frontend_time = payload.frontend_time;
          }
          if (payload.start_time) {
            metrics.start_time = payload.start_time;
          }
          try {
            Object.keys(payload.metrics).forEach(key => {
              const value = payload.metrics[key];
              Object.keys(value).forEach(k => {
                if (typeof value[k] === 'number') {
                  metrics[`${key}_${k}`] = value[k];
                }
              });
            });
          } catch (e) {}
          this.sendCustom(name, metrics, tags);
          this.customInteractionTags[`${actionType}:${name}`] = undefined;
          // 不再用 action 上报
          return;
        }
        if (name && actionType && tags) {
          ev.payload.categories = tags;
          this.customInteractionTags[`${actionType}:${name}`] = undefined;
        }
      }
      return ev;
    });
  }

  async waitInitConfig() {
    if (this.initConfigPromise) {
      return this.initConfigPromise.promise;
    }
    this.initConfigPromise = createPromiseObj();
    let hasInitConfig = false;
    const initConfig = () => {
      if (hasInitConfig) {
        return;
      }
      hasInitConfig = true;
      // 写入一些从 server 中获取的 user 信息
      slardar.config({
        userId: window.userInfo?.username || window.userInfo?.nickname,
      });
      window[SLARDAR_CLIENT]?.('context.merge', getSlardarInitContexts());
      this.initConfigPromise.resolve();
    };
    if (window.streamService) {
      window.streamService.onStreamData(() => {
        if (!window.userInfo || !window.userLoginInfo) {
          return;
        }
        initConfig();
      });
      window.streamService.onStreamEnd(initConfig);
    } else {
      initConfig();
    }
    return this.initConfigPromise.promise;
  }

  async getConfig() {
    if (!window[SLARDAR_CLIENT]) {
      return {};
    }
    await this.initPromise;
    return window[SLARDAR_CLIENT].config();
  }

  config(config: Partial<SlardarConfig> = {}) {
    window[SLARDAR_CLIENT]?.('config', {
      ...config,
    });
  }

  setContext(key: string, value: any) {
    window[SLARDAR_CLIENT]?.('context.set', key, value);
  }

  addInterceptor(fn: (ev) => boolean) {
    this.interceptors.push(fn);
    return () => {
      this.interceptors = this.interceptors.filter(f => f !== fn);
    };
  }

  // 未调用 start 方法时，SDK 已经开始监控，但是数据只会存在暂存区，不会有任何上报
  start() {
    if (this._started) {
      return;
    }
    this._started = true;
    window[SLARDAR_CLIENT]?.('start');
  }

  sendPV() {
    window[SLARDAR_CLIENT]?.('send', 'pageview');
  }

  sendJSError(error, message) {
    // window[SLARDAR_CLIENT]?.('Sentry', Sentry => {
    //   Sentry.captureException(error);
    //   Sentry.captureMessage(message);
    // });
    let data = {};
    try {
      data =
        typeof message === 'string'
          ? {
              // 额外上报一些上下文
              message,
            }
          : JSON.parse(JSON.stringify(message) || '{}');
    } catch {}
    window[SLARDAR_CLIENT]?.('captureException', error, data);
  }

  async sendLog(
    level: 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace',
    content: string | Record<string, unknown>,
    extra: Record<string, unknown> = {},
  ) {
    await this.waitInitConfig();
    if (window[SLARDAR_CLIENT]) {
      window[SLARDAR_CLIENT]('sendLog', {
        level,
        content: replaceSensitiveWord(
          typeof content === 'string' ? content : JSON.stringify(content || {}),
        ),
        extra,
      });
    }
  }

  /**
   * * 新版本只有 sendEvent 方法，最好别用这个方法了
   * @param name
   * @param value
   * @param tags
   * @deprecated 统一采用 sendCustom 方法
   */
  sendCount(name: string, value: number, tags?: Record<string, string>) {
    this.sendCustom(
      name,
      {
        count: value,
      },
      tags,
    );
  }

  /**
   * 新版本只有 sendEvent 方法，最好别用这个方法了
   * @param name
   * @param value
   * @param tags
   * @deprecated 统一采用 sendCustom 方法
   */
  sendTiming(name: string, value: number, tags?: Record<string, string>) {
    this.sendCustom(
      name,
      {
        time: value,
      },
      tags,
    );
  }

  /**
   * @param name 事件名, 可自定义
   * @param metrics  metrics为数值型信息，key的数量不超过1000
   * @param tags tags 为维度型信息，key的数量不超过100
   */
  async sendCustom(name: string, metrics: Record<string, number>, tags?: Record<string, string>) {
    await this.waitInitConfig();
    window[SLARDAR_CLIENT]?.('sendEvent', {
      name,
      metrics,
      categories: tags,
    });
  }

  /**
   * 自定义行为上报
   * @param name 行为名称， 自定义
   * @param action_type 行为类型 比如：click、scroll、input 等, 可以自定义
   * @param tags 自定义维度信息
   */
  collectActionInfo(name: string, action_type: Exclude<string, 'click'>) {
    if (action_type === 'click' || !name || !action_type) {
      return;
    }
    window[SLARDAR_CLIENT]?.('startAction', name, action_type);
  }

  /**
   * 借用slardar action的监听机制，最终使用custom event上报
   * @param name 行为名称， 自定义
   * @param tags 自定义维度信息
   */
  collectActionInfoWithCustomEvent(name: string, tags?: Record<string, string>) {
    if (!name) {
      return;
    }
    if (tags) {
      this.customInteractionTags[`${SLARDAR_ACTION_WITH_CUSTOM_EVENT_TYPE}:${name}`] = tags;
    }
    window[SLARDAR_CLIENT]?.('startAction', name, SLARDAR_ACTION_WITH_CUSTOM_EVENT_TYPE);
  }
}

const slardar = new Slardar();
export { slardar };
